import { zodResolver } from '@hookform/resolvers/zod';
import SendIcon from '@mui/icons-material/Send';
import {
    Box,
    Card,
    FormHelperText,
    Grid,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as z from 'zod';
import {
    useAiGuestReservationRequestMutation,
    useGetAIConversationIdMutation,
    useGetAllAssetsQuery,
    useGetCompanyGuestsInfoQuery,
    useGetIndividualAssetQuery,
    useGetIndividualConversationQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import FormTextField from '../../../shared/FormTextField';
import ExperiencesListAll from '../../Experiences/ExperiencesListAll';
import AICalendar from './AICalendar';
import StaffAttending from './StaffAttending';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import RefreshModal from './RefreshModal';
import {
    selectAiConversationId,
    setAiConversationId,
} from '../../../../app/Slices/AiConversationIdSlice';
import {
    addMessage,
    selectAiMessages,
    setMessages,
    updateLastMessage,
} from '../../../../app/Slices/AiChatMessagesSlice';
import { setAiChat } from '../../../../app/Slices/AiAssistantSlice';
import AvailabilityTable from './AvailabilityTable';
import { DEFAULT_TEMPLATE } from '../ReservationRequest/FormSections/ReservationRequestSections/RsvpConfig';

interface Props {
    isSmallChat?: boolean;
}

export const availability = (
    message: any,
): {
    showTable: boolean;
    hasAvailableDates: boolean;
    availabilityCount: number;
    showCalendar: boolean;
} => {
    const lowerCasedMessage = message.message?.toLowerCase?.() || '';
    const words = ['isAvailable', 'reason', 'availability'];

    const showTable = words.every((word) =>
        lowerCasedMessage.includes(word.toLowerCase()),
    );

    let hasAvailableDates = false;
    let availabilityCount = 0;
    let showCalendar = false;

    try {
        const parsed = JSON.parse(message.message);
        const availabilityArray = parsed.availability || [];

        availabilityCount = Array.isArray(availabilityArray)
            ? availabilityArray.length
            : 0;

        hasAvailableDates = availabilityArray.some(
            (date: any) => date.isAvailable,
        );

        showCalendar = availabilityArray.some(
            (date: any) => date.showCalendar === true,
        );
    } catch (e) {
        hasAvailableDates = false;
        availabilityCount = 0;
        showCalendar = false;
    }
    return {
        showTable,
        hasAvailableDates,
        availabilityCount,
        showCalendar,
    };
};

export default function AIGuestReservationsAssistant({ isSmallChat }: Props) {
    const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false);
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const aiConversationId: any = useSelector(selectAiConversationId);

    const { data: guestCompanyInfo } = useGetCompanyGuestsInfoQuery(
        `/guests/${loggedInUser?.company}`,
        {
            skip:
                loggedInUser?.role === 'admin' ||
                loggedInUser?.role.includes('owner'),
        },
    );

    const [experienceFilters, setExperienceFilters] = useState({});

    const { data: allExperiences, isFetching: isFetchingExperiences } =
        useGetAllAssetsQuery({
            url: '/modules',
            ...experienceFilters,
            sort: 'name',
        });

    const [showTextBox, setShowTextBox] = useState(true);
    const schema = z.object({
        outgoingMessage: z.string(),
        renderMessage: z.boolean().default(true),
        disableSendButton: z.boolean().default(false),
        staffAttending: z
            .array(
                z.object({
                    id: z.number(),
                    email: z.string(),
                    firstName: z.string(),
                    lastName: z.string(),
                }),
            )
            .default([]),
    });
    type schemaType = z.infer<typeof schema>;
    const methods = useForm<schemaType>({
        resolver: zodResolver(schema),
        defaultValues: { outgoingMessage: '' },
    });

    const { control, handleSubmit, reset, watch, setValue } = methods;
    // Create a ref for the messages container
    const messagesEndRef = useRef<any>(null);

    const staffAttending = watch('staffAttending');
    const [staffLength, setStaffLength] = useState(0);
    const [charges, setCharges] = useState<any>();

    const location = useLocation();
    const navigate = useNavigate();
    //------//
    const [moduleId, setModuleId] = useState<any>(undefined);
    const [experiences, setExperiences] = useState<any[]>([]);
    const [pendingMessage, setPendingMessage] = useState('');
    const [nextPageFn, setNextPageFn] = useState<() => void>();

    const [getConversationId]: any = useGetAIConversationIdMutation();
    const [aiReservationRequest, { isLoading }]: any =
        useAiGuestReservationRequestMutation();
    const [mainDetails, setMainDetails]: any = useState();
    const [aiResponse, setAiResponse]: any = useState();
    const messages = useSelector(selectAiMessages);
    const [isTyping, setIsTyping] = useState(false);
    const dispatch = useDispatch();
    const [pendingFilterMessage, setPendingFilterMessage] = useState(false);

    const rsvpConfig = {
        rsvpEnabled: false,
        rsvpMessage: DEFAULT_TEMPLATE,
        autoRemindersEnabled: true,
        reminderDaysBeforeEvent: 7,
    };

    const {
        data: messagesHistory,
        isFetching,
        error,
    } = useGetIndividualConversationQuery(
        `ai/conversations/find-by-conversation-id/${aiConversationId}`,
        { skip: messages.length < 3 },
    );

    const { data: experience, isLoading: experienceLoading } =
        useGetIndividualAssetQuery(
            `/modules/${mainDetails?.module?.id || moduleId}`,
            {
                skip: !moduleId && !mainDetails?.module?.id,
            },
        );

    useEffect(() => {
        if (!moduleId && !mainDetails?.module?.id && messages.length > 0) {
            const lastMessage = messages[messages.length - 1].message;

            const moduleIdFromMessages = lastMessage.includes('moduleId')
                ? JSON.parse(lastMessage)?.moduleId
                : undefined;

            if (moduleIdFromMessages) setModuleId(moduleIdFromMessages);
        }
    }, [messages]);

    useEffect(() => {
        setNextPageFn(undefined);

        if (location?.state?.page !== undefined) {
            if (location?.state?.page === 'reservationPage') {
                setModuleId(undefined);
            } else {
                setModuleId(location?.state?.id);
            }
        }
    }, [location]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);

        return () => clearTimeout(timeout);
    }, [messages, staffLength, charges, mainDetails]);

    const billingInfoMessage =
        loggedInUser?.role === 'guest_admin'
            ? 'Please set up your billing details to make bookings.'
            : `Contact your company admin - ${guestCompanyInfo?.email} to set up your billing details to make bookings.`;

    const chatInitiated = () => {
        const message = {
            message: `${
                !guestCompanyInfo?.billingDetailsAdded
                    ? billingInfoMessage
                    : location?.state?.id !== undefined
                      ? `Hello, tell us when you would like to reserve ${location?.state?.name}?`
                      : `Hello! Tell us what type of experience you'd like to reserve:
                        1 - Property
                        2 - Boat
                        3 - Office Space
                        4 - Vehicle
                        5 - Activity

                        You can also filter by Australian states or suburbs.`
            }`,
            sentTime: 'just now',
            sender: '7DAY Assistant',
        };

        setTimeout(function () {
            dispatch(setMessages([message]));

            setIsTyping(false);
        }, 2000);
    };

    useEffect(() => {
        if (pendingFilterMessage && allExperiences && !isFetchingExperiences) {
            const newMessage = {
                message:
                    'Please tell us which experience you would like to reserve? Here are the options based on your filters.',
                direction: 'just now',
                sender: '7DAY Assistant',
                isFilterFunction: true,
                experiences: allExperiences,
            };
            dispatch(addMessage(newMessage));

            setPendingFilterMessage(false);
            setIsTyping(false);
        }
    }, [pendingFilterMessage, allExperiences, isFetchingExperiences]);

    const onSubmit = (data: FieldValues) => {
        if (data.outgoingMessage === '') return;

        setPendingMessage('');

        const newMessage = {
            message: data.outgoingMessage,
            direction: 'outgoing',
            sender: loggedInUser?.sub.toString(),
            renderMessage: data.renderMessage,
        };

        if (data?.disableSendButton) {
            dispatch(updateLastMessage({ disableSendButton: true }));
        }

        dispatch(addMessage(newMessage));
        setIsTyping(true);

        const element: any = document.getElementById('messages');
        element?.scroll({
            top: 100,
            left: 100,
            behavior: 'smooth',
        });

        const requestBody = {
            conversationId: aiConversationId,
            message: data.outgoingMessage,
            ...(moduleId !== undefined && { moduleId }),
        };

        aiReservationRequest(requestBody).then((data: any) => {
            setIsTyping(true);

            if (data) {
                if (data?.error?.status === 400) {
                    setIsTyping(false);

                    const message = data?.error?.data.message.includes(
                        'successful',
                    )
                        ? 'An error occurred while making the reservation. Please try again.'
                        : data?.error?.data.message;

                    dispatch(
                        addMessage({
                            message: message,
                            direction: 'just now',
                            sender: '7DAY Assistant',
                        }),
                    );

                    if (data?.error?.data?.moduleId) {
                        setModuleId(data?.error?.data?.moduleId);
                    }
                } else if (data?.error?.status === 500) {
                    dispatch(
                        addMessage({
                            message:
                                'There was an error in the server. Please try again.',
                            direction: 'just now',
                            sender: '7DAY Assistant',
                        }),
                    );
                    setIsTyping(false);
                } else {
                    if (data?.data?.functionType === 'experience_filters') {
                        setExperienceFilters({
                            ...experienceFilters,
                            type: data?.data?.types,
                            state: data?.data?.states,
                            suburb: data?.data?.suburbs,
                            visitType: data?.data?.visitType,
                            guests: data?.data?.guests,
                            overnightGuests:
                                data?.data?.overnightGuests ??
                                data?.data?.guests,
                            bedrooms: data?.data?.bedrooms,
                            amenities: data?.data?.amenities,
                            checkinDate: data?.data?.checkinDate,
                            checkoutDate: data?.data?.checkoutDate,
                        });

                        setPendingFilterMessage(true);
                        setIsTyping(false);
                    }

                    if (data?.data?.bookingInfo && !data?.data?.availability) {
                        setMainDetails(data?.data);
                    }

                    if (data?.data?.availability) {
                        const newMessage = {
                            message: JSON.stringify(data?.data),
                            direction: 'just now',
                            sender: '7DAY Assistant',
                        };
                        setModuleId(data?.data.moduleId);
                        dispatch(addMessage(newMessage));
                    }

                    setIsTyping(false);
                }
            } else {
                setIsTyping(false);
            }
        });

        reset();
    };

    useEffect(() => {
        if (messagesHistory?.messages && !mainDetails) {
            const result = messagesHistory.messages.find(
                (message: any) =>
                    message.message.includes('bookingInfo') &&
                    !message.message.includes('availability'),
            );

            if (result) {
                setMainDetails(JSON.parse(result.message));
            }
        }
    }, [messagesHistory, mainDetails]);

    useEffect(() => {
        if (
            mainDetails &&
            experience &&
            !aiResponse &&
            !messages.find((message) => isStaffAttendingQuestion(message))
        ) {
            if (experience.visitType === 'business') {
                if (
                    ['property', 'boat', 'activity'].includes(experience.type)
                ) {
                    const newMessage = {
                        message:
                            'Who will be attending this reservation? You can select from the following list',
                        direction: 'just now',
                        sender: '7DAY Assistant',
                    };
                    dispatch(addMessage(newMessage));
                } else {
                    setAiResponse({
                        ...mainDetails,
                        aiConversationId,
                        policies: [],
                        spaces: [],
                        rsvpConfig,
                        privateMessages: [],
                        booker: { id: loggedInUser?.sub },
                        bookingInfo: {
                            ...mainDetails.bookingInfo,
                            driverSameAsBooker: true,
                        },
                    });
                }
            } else {
                setAiResponse({
                    ...mainDetails,
                    aiConversationId,
                    policies: [],
                    spaces: [],
                    rsvpConfig,
                    privateMessages: [],
                    booker: { id: loggedInUser?.sub },
                    bookingInfo: {
                        ...mainDetails.bookingInfo,
                        driverSameAsBooker: true,
                    },
                });
            }
        }
        if (aiResponse) {
            handleRefresh();
            dispatch(setAiChat(false));

            navigate(
                `/experiences/reservationRequest/${aiResponse?.bookingInfo?.moduleType}/${aiResponse?.module?.name}/${aiResponse?.module?.id}`,
                {
                    state: {
                        page: 'aiGuestReservation',
                        reservationData: aiResponse,
                        dates: aiResponse.dates,
                    },
                },
            );
        }
    }, [aiResponse, mainDetails, experience]);

    useEffect(() => {
        if (!messages.length) return;

        const lastMessage = messages[messages.length - 1];
        const existingExperiences = lastMessage.experiences;

        const shouldUpdate =
            !existingExperiences ||
            existingExperiences.length !== experiences.length;

        if (!shouldUpdate) return;

        const updatedMessages = [...messages];
        updatedMessages[updatedMessages.length - 1] = {
            ...lastMessage,
            experiences,
        };
        setMessages(updatedMessages);
    }, [experiences]);

    useEffect(() => {
        if (!isFetchingExperiences && allExperiences) {
            setExperiences(allExperiences);
        }
    }, [isFetchingExperiences, allExperiences]);

    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!nextPageFn) return;

        const el = e.currentTarget;

        const top = el.scrollHeight - el.clientHeight;
        const scroll = -el.scrollTop;

        if (top - scroll <= 1) {
            nextPageFn();
        }
    };

    useEffect(() => {
        const latestMessage = messages[messages.length - 1];

        if (
            messages.length > 0 &&
            (isStaffAttendingQuestion(latestMessage) ||
                (messages.length > 0 &&
                    availability(latestMessage).showCalendar))
        ) {
            setShowTextBox(false);
        } else {
            setShowTextBox(true);
        }

        if (
            messages.length > 0 &&
            !latestMessage.isFilterFunction &&
            ['here are the', 'here is the'].some((word) =>
                latestMessage.message.toLowerCase().includes(word),
            )
        ) {
            setExperienceFilters({});

            const filteredExperiences = allExperiences?.filter((experience) =>
                latestMessage.message
                    .toLowerCase()
                    .includes(experience.name.toLowerCase()),
            );
            if (filteredExperiences) {
                setExperiences(filteredExperiences);
            }
        }
    }, [messages]);

    const staffAttendingKeywords = ['attending', 'following', 'list'];

    const isStaffAttendingQuestion = (message: any): boolean => {
        return (
            staffAttendingKeywords.every((word: string) =>
                message.message.toLowerCase().includes(word.toLowerCase()),
            ) && message.sender.includes('7DAY')
        );
    };

    const showRetryButton = (message: any): boolean => {
        return message.message.toLowerCase().includes('error');
    };

    const showExperiencesList = (message: any): boolean => {
        const result =
            message.message
                .toLowerCase()
                .includes(
                    'please tell us which experience you would like to reserve? Here are the options based on your filters'.toLowerCase(),
                ) ||
            ['here are the available', 'here is the available'].some((word) =>
                message.message.toLowerCase().includes(word),
            );

        return result;
    };

    useEffect(() => {
        if (staffAttending) {
            setAiResponse({
                ...mainDetails,
                aiConversationId,
                staffAttending,
                policies: [],
                spaces: [],
                rsvpConfig,
                privateMessages: [],
                booker: { id: loggedInUser?.sub },
            });
        }
    }, [staffAttending]);

    const retry = () => {
        dispatch(updateLastMessage({ disableRetry: true }));
        setValue(
            'outgoingMessage',
            'Retry booking with the above information.',
        );
        setValue('renderMessage', false);
        handleSubmit(onSubmit)();
    };

    const submitSampleQuestion = (question: string) => {
        setValue('outgoingMessage', question);
        handleSubmit(onSubmit)();
    };

    const exampleQuestions: string[] =
        location?.state?.id !== undefined
            ? []
            : [
                  'Show me all properties in NSW',
                  'Book "The Dancing Queen"',
                  'Show me properties in Terrigal that accommodate 10+ adults',
              ];

    const handleRefresh = () => {
        dispatch(setMessages([]));
        getConversationId().then((data: any) => {
            dispatch(setAiConversationId(data?.data?.id));
        });
        setIsTyping(true);
        chatInitiated();
        setShowRefreshModal(false);
    };

    useEffect(() => {
        if (
            !aiConversationId ||
            location?.state?.page === 'experience' ||
            location?.state?.page === 'reservationsPage' ||
            messages?.length === 1
        ) {
            handleRefresh();
        }
    }, []);

    const handleMinimize = () => {
        dispatch(setAiChat(true));
        navigate(`/viewExperiences/business`);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if ((isMobile || isIpad) && e.key === 'Enter') {
            // e.preventDefault();
        } else if (e.key === 'Enter' && !e.shiftKey && !isTyping) {
            e.preventDefault();
            handleSubmit(onSubmit)();
        }
    };

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={handleSubmit(onSubmit)}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Grid
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            top: 0,
                            right: 0,
                            zIndex: 1000,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%',
                            bgcolor: 'none',
                        }}
                    >
                        {!isSmallChat && (
                            <Grid
                                sx={{
                                    width: '100%',
                                    justifyContent: 'left',
                                    display: 'flex',
                                }}
                            >
                                <Typography
                                    variant='h1'
                                    sx={{
                                        textAlign: 'left',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    AI Reservation Assistant
                                </Typography>
                            </Grid>
                        )}
                        <Typography
                            color='primary'
                            onClick={() => setShowRefreshModal(true)}
                            sx={{
                                position: 'absolute',
                                zIndex: 10,
                                right: isSmallChat ? 20 : 30,
                                cursor: 'pointer',
                                color: 'text.primary',
                            }}
                        >
                            <RefreshIcon />{' '}
                        </Typography>
                        {!isSmallChat && (
                            <Typography
                                color='primary'
                                onClick={handleMinimize}
                                sx={{
                                    position: 'absolute',
                                    zIndex: 10,
                                    right: 0,
                                    cursor: 'pointer',
                                    color: 'text.primary',
                                }}
                            >
                                <CloseFullscreenIcon />{' '}
                            </Typography>
                        )}
                    </Box>
                    <Stack
                        spacing={2}
                        flex={1}
                        onScroll={onScroll}
                        sx={{
                            maxHeight: isSmallChat ? '550px' : 'none',
                            paddingBottom:
                                messages?.length > 1 && !isSmallChat
                                    ? '100px'
                                    : messages?.length === 0
                                      ? '500px'
                                      : messages?.length === 1
                                        ? '200px'
                                        : '100px',
                            paddingTop: '50px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            width: '100%',
                            pr: 1,
                            bgcolor: 'none',
                            borderRadius: '2px',
                        }}
                    >
                        {guestCompanyInfo?.onCreditHold ? (
                            <Typography sx={{ color: 'red' }}>
                                You are on credit hold as your account is now
                                overdue. Please pay to continue using 7DAY.
                            </Typography>
                        ) : (
                            messages?.map((message: any, index: any) => {
                                const isUserMessage =
                                    loggedInUser?.sub.toString() ===
                                    message?.sender;
                                return (
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        // width='fit-content'
                                        width='100%'
                                        alignSelf={
                                            loggedInUser?.sub.toString() ===
                                            message?.sender
                                                ? 'flex-end'
                                                : 'flex-start'
                                        }
                                        alignItems={
                                            loggedInUser?.sub.toString() ===
                                            message?.sender
                                                ? 'flex-end'
                                                : 'flex-start'
                                        }
                                    >
                                        {message.renderMessage !== false && (
                                            <>
                                                <Box
                                                    id='messages'
                                                    bgcolor={
                                                        isUserMessage
                                                            ? 'chatComponent.userBackground'
                                                            : 'chatComponent.assistantBackground'
                                                    }
                                                    sx={{
                                                        py: 1.5,
                                                        px: 2,
                                                        maxWidth: '70%',
                                                        borderRadius: 4.5,
                                                        ...(isUserMessage
                                                            ? {
                                                                  borderBottomRightRadius: 0,
                                                              }
                                                            : {
                                                                  borderBottomLeftRadius: 0,
                                                              }),
                                                    }}
                                                >
                                                    <Typography
                                                        color={
                                                            isUserMessage
                                                                ? 'chatComponent.userText'
                                                                : 'chatComponent.assistantText'
                                                        }
                                                        sx={{
                                                            whiteSpace:
                                                                'pre-line',
                                                        }}
                                                    >
                                                        {' '}
                                                        {showExperiencesList(
                                                            message,
                                                        ) &&
                                                        message?.experiences &&
                                                        message?.experiences
                                                            ?.length === 0 &&
                                                        !isFetchingExperiences
                                                            ? 'No experiences match your filters. Try adjusting your criteria to see more options.'
                                                            : availability(
                                                                    message,
                                                                ).showTable &&
                                                                availability(
                                                                    message,
                                                                )
                                                                    .availabilityCount ===
                                                                    1
                                                              ? `${
                                                                    JSON.parse(
                                                                        message.message,
                                                                    )
                                                                        .availability[0]
                                                                        .reason
                                                                } ${
                                                                    availability(
                                                                        message,
                                                                    )
                                                                        .showCalendar
                                                                        ? 'Please use the following calendar to check the availability and select the date and time.'
                                                                        : ''
                                                                } `
                                                              : availability(
                                                                      message,
                                                                  ).showTable &&
                                                                  availability(
                                                                      message,
                                                                  )
                                                                      .hasAvailableDates
                                                                ? 'Some of the dates you have selected are unavailable. If you want to proceed with the available dates click submit.'
                                                                : availability(
                                                                        message,
                                                                    )
                                                                        .showTable &&
                                                                    !availability(
                                                                        message,
                                                                    )
                                                                        .hasAvailableDates
                                                                  ? 'None of the dates you have selected are available. You can select dates from the following calendar and click submit.'
                                                                  : message.message}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant='subtitle2'
                                                    color='textSecondary'
                                                    sx={{
                                                        fontSize: '10px',
                                                        padding: '5px',
                                                    }}
                                                >
                                                    {message?.sender ===
                                                    '7DAY Assistant'
                                                        ? message?.sender
                                                        : 'You'}
                                                </Typography>
                                            </>
                                        )}
                                        {availability(message).showTable &&
                                            availability(message)
                                                .availabilityCount > 1 && (
                                                <Grid
                                                    sx={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent:
                                                            'flex-end',
                                                        minHeight: 500,
                                                    }}
                                                >
                                                    {
                                                        <Grid
                                                            sx={{
                                                                width: '100%',
                                                                maxWidth:
                                                                    '1000px',
                                                            }}
                                                        >
                                                            <AvailabilityTable
                                                                handleSubmitMain={handleSubmit(
                                                                    onSubmit,
                                                                )}
                                                                setCharges={
                                                                    setCharges
                                                                }
                                                                charges={
                                                                    charges
                                                                }
                                                                isSmallChat={
                                                                    isSmallChat
                                                                }
                                                                availabilityResponse={JSON.parse(
                                                                    message.message,
                                                                )}
                                                                bookingInfo={
                                                                    JSON.parse(
                                                                        message.message,
                                                                    )
                                                                        ?.bookingInfo
                                                                }
                                                                moduleId={
                                                                    JSON.parse(
                                                                        message.message,
                                                                    ).moduleId
                                                                }
                                                                disableSubmit={
                                                                    messages.length -
                                                                        1 !==
                                                                    index
                                                                }
                                                            />
                                                        </Grid>
                                                    }
                                                </Grid>
                                            )}

                                        {availability(message).showCalendar &&
                                            availability(message)
                                                .availabilityCount === 1 && (
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        width: '100%',
                                                        justifyContent:
                                                            'flex-end',
                                                        minHeight: '420px',
                                                    }}
                                                >
                                                    {experience && (
                                                        <AICalendar
                                                            setCharges={
                                                                setCharges
                                                            }
                                                            charges={charges}
                                                            experience={
                                                                experience
                                                            }
                                                            handleSubmitMain={handleSubmit(
                                                                onSubmit,
                                                            )}
                                                            disableSendButton={
                                                                message?.disableSendButton
                                                            }
                                                            isSmallChat={
                                                                isSmallChat
                                                            }
                                                        />
                                                    )}
                                                </Grid>
                                            )}
                                        {showRetryButton(message) && (
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <CustomButton
                                                    variant='contained'
                                                    color='neutral'
                                                    disabled={
                                                        message.disableRetry
                                                    }
                                                    onClick={retry}
                                                >
                                                    Retry
                                                </CustomButton>
                                            </Grid>
                                        )}

                                        {isStaffAttendingQuestion(message) && (
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <StaffAttending
                                                    adults={
                                                        mainDetails?.bookingInfo
                                                            ?.adults
                                                    }
                                                    handleSubmitMain={handleSubmit(
                                                        onSubmit,
                                                    )}
                                                    disableSendButton={
                                                        message?.disableSendButton
                                                    }
                                                    isSmallChat={isSmallChat}
                                                    setStaffLength={
                                                        setStaffLength
                                                    }
                                                />
                                            </Grid>
                                        )}
                                        {showExperiencesList(message) &&
                                            message.experiences &&
                                            !isFetchingExperiences && (
                                                <>
                                                    {message.experiences
                                                        .length < 8 ||
                                                    isSmallChat ? (
                                                        <Grid
                                                            container
                                                            spacing={4}
                                                            columns={60}
                                                            sx={{
                                                                flexGrow: 1,
                                                                width: '100%',
                                                                display: 'flex',
                                                            }}
                                                        >
                                                            <ExperiencesListAll
                                                                experiences={
                                                                    message.experiences
                                                                }
                                                                visitType=''
                                                                component='aiGuestReservation'
                                                                isSmallChat={
                                                                    isSmallChat
                                                                }
                                                            />{' '}
                                                        </Grid>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'center',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Card
                                                                sx={{
                                                                    pt: 5,
                                                                    pl: 5,
                                                                    pb: 5,
                                                                    maxHeight:
                                                                        '600px',
                                                                    width: '99.3%',
                                                                    overflow:
                                                                        'auto',
                                                                }}
                                                            >
                                                                <Grid
                                                                    container
                                                                    spacing={4}
                                                                    columns={60}
                                                                    sx={{
                                                                        flexGrow: 1,

                                                                        width: '100%',
                                                                        display:
                                                                            'flex',
                                                                    }}
                                                                >
                                                                    <ExperiencesListAll
                                                                        experiences={
                                                                            message.experiences
                                                                        }
                                                                        visitType=''
                                                                        component='aiGuestReservation'
                                                                    />{' '}
                                                                </Grid>{' '}
                                                            </Card>
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                    </Box>
                                );
                            })
                        )}

                        {isTyping ? (
                            <Box
                                display='flex'
                                flexDirection='row'
                                width='fit-content'
                                maxWidth='90%'
                                alignSelf={'flex-start'}
                                alignItems={'flex-start'}
                                gap={0.3}
                                pt={1}
                            >
                                {' '}
                                <Skeleton
                                    variant='circular'
                                    width={8}
                                    height={8}
                                    sx={{ bgcolor: 'dark.main' }}
                                />
                                <Skeleton
                                    variant='circular'
                                    width={8}
                                    height={8}
                                    sx={{ bgcolor: 'dark.main' }}
                                />
                                <Skeleton
                                    variant='circular'
                                    width={8}
                                    height={8}
                                    sx={{ bgcolor: 'dark.main' }}
                                />
                            </Box>
                        ) : null}
                        {/* Reference for scrolling */}
                        <div ref={messagesEndRef} />
                    </Stack>
                    <Stack
                        sx={{
                            position: isSmallChat ? 'absolute' : 'fixed',
                            bottom: 0,
                            ...(isSmallChat && {
                                left: 0,
                                right: '20px',
                            }),
                            py: 1,
                            mb: 1,
                            width: isSmallChat ? 'default' : '100%',
                            zIndex: 10,
                            borderRadius: 2,
                        }}
                    >
                        {showTextBox && messages.length <= 1 && (
                            <Box
                                gap={1}
                                pt={2}
                                sx={{
                                    backgroundColor: 'none',
                                    width: isSmallChat ? '100%' : '93%',
                                    maxWidth: isSmallChat ? '370px' : '100%',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'flex-end',
                                    paddingBottom: 1,
                                    px: 1,
                                }}
                            >
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    {' '}
                                    {messages.length <= 1 &&
                                        exampleQuestions.map((question) => (
                                            <CustomButton
                                                size={
                                                    isSmallChat
                                                        ? 'small'
                                                        : 'auto'
                                                }
                                                variant='outlined'
                                                color='primary'
                                                disabled={
                                                    isTyping ||
                                                    !guestCompanyInfo?.billingDetailsAdded ||
                                                    guestCompanyInfo?.onCreditHold
                                                }
                                                onClick={() =>
                                                    submitSampleQuestion(
                                                        question,
                                                    )
                                                }
                                            >
                                                {question}
                                            </CustomButton>
                                        ))}
                                </Grid>
                            </Box>
                        )}
                        {showTextBox && (
                            <Box
                                sx={{
                                    backgroundColor: 'primary.main',
                                    width: isSmallChat ? '100%' : '93%',
                                    maxWidth: isSmallChat ? '370px' : '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 1,
                                }}
                            >
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        width: '100%',
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    <Grid
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            width: '100%',
                                        }}
                                    >
                                        <FormTextField
                                            name='outgoingMessage'
                                            placeholder={
                                                pendingMessage !== ''
                                                    ? ''
                                                    : 'Type a message...'
                                            }
                                            control={control}
                                            multiline
                                            maxRows={8}
                                            inputProps={{
                                                maxLength: 300,
                                            }}
                                            disabled={
                                                guestCompanyInfo?.onCreditHold
                                            }
                                            onKeyDown={handleKeyDown}
                                            sx={{
                                                width: '100%',
                                            }}
                                        />{' '}
                                    </Grid>

                                    <Grid sx={{ flexShrink: 0 }}>
                                        <CustomButton
                                            color='secondary'
                                            variant='contained'
                                            // onClick={onSubmit}
                                            disabled={
                                                messages.length === 0 ||
                                                isTyping ||
                                                !guestCompanyInfo?.billingDetailsAdded ||
                                                guestCompanyInfo?.onCreditHold
                                            }
                                            type='submit'
                                            disableElevation
                                            disableRipple
                                            sx={{
                                                height: '40px',
                                                minHeight: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    color: 'white',
                                                    backgroundColor:
                                                        'text.main',
                                                },
                                            }}
                                        >
                                            <SendIcon />
                                        </CustomButton>
                                    </Grid>
                                </Grid>
                                {!(isMobile || isIpad) && (
                                    <FormHelperText
                                        sx={{
                                            ml: 'auto',
                                            pr: 10,
                                            fontWeight: 500,
                                            color: 'primary.light',
                                        }}
                                    >
                                        Shift + Enter for new line
                                    </FormHelperText>
                                )}
                            </Box>
                        )}
                    </Stack>
                </Grid>

                <RefreshModal
                    open={showRefreshModal}
                    onChange={handleRefresh}
                    onClose={() => setShowRefreshModal(false)}
                />
            </form>
        </FormProvider>
    );
}
